// =============================================================================
// Guided lessons — the single source for in-app tours AND the written guides
// on the Learn tab. Keep them in step with the screens they explain:
//   - every `target` must match a data-tour="…" attribute in the code
//     (npm run check-tours, also run before every build, fails otherwise)
//   - when you change a screen a lesson covers, update its steps in the
//     same change
// =============================================================================

export type TourAdvance =
  | { kind: 'next' }                 // user presses Next
  | { kind: 'click' }                // user clicks the highlighted element
  | { kind: 'event'; name: string }; // the app emits tourEvent(name)

export interface TourStep {
  id: string;
  /** Path the step lives on; a prefix match ('/project/' covers any project). */
  route: string;
  /** data-tour anchor(s) to spotlight — several are lit as one area. Omit for a corner card. */
  target?: string | string[];
  title: string;
  body: string;
  advance: TourAdvance;
  /** Shown under the body while waiting for the user to act. */
  hint?: string;
  /** "Do it for me" button: sends lessonAction(name) to the screen. */
  action?: { label: string; name: string };
  /** 'center' puts the card in the middle of the screen (e.g. an intro). */
  /** 'side': beside the target, clear of a dropdown that opens below it. */
  placement?: 'center' | 'side';
  /** The target may legitimately not exist (e.g. no company has 0 hiring
   *  managers): after a few seconds show the card centred, without a
   *  "can't find it" warning. */
  optional?: boolean;
  /** Only some people have the target (e.g. a Starter-only card): when it
   *  isn't on screen, the step is passed by silently. */
  skipIfMissing?: boolean;
  /** Guides: a screenshot shown under the step on the Learn tab (from public/). */
  image?: string;
}

export interface Lesson {
  id: string;
  title: string;
  summary: string;
  minutes: number;
  /** Where the lesson starts. */
  startPath: string;
  /** Start on the user's copy of the sample project (from "Build your first
   *  search") instead of startPath. */
  startOnSampleProject?: boolean;
  /** Needs Search access (universe) to run. */
  needsSearch?: boolean;
  /** A written guide only (no in-app tour): for things done mostly outside
   *  MarketMapr, e.g. in Claude, where a lesson card can't follow. */
  guideOnly?: boolean;
  /** Needs the Claude connector: Growth / Scale, or a Starter month (offered or running). */
  needsConnector?: boolean;
  /** Folder on the Learn tab (LESSON_FOLDERS id). Omit for a top-level lesson. */
  folder?: string;
  /** Not a tour: the card's Explore button opens this Learn tab instead
   *  (e.g. 'claude' = Using Claude). MarketMapr only. */
  opensTab?: 'claude';
  /** Runs on any project: the sample project when the user has one (made
   *  for them if they have Search), else their most recent project. */
  anyProject?: boolean;
  steps: TourStep[];
}

export interface LessonFolder {
  id: string;
  title: string;
  summary: string;
}

// Folders of short, single-task lessons on the Learn tab — each lesson
// stands alone so help can point straight at the one that answers a question.
export const LESSON_FOLDERS: LessonFolder[] = [
  {
    id: 'project',
    title: 'Your project',
    summary: 'Short lessons on everything in a project: the three connected lists, viewing, searching and filtering, columns, companies, tags, subprojects and export.',
  },
];

export const LESSONS: Lesson[] = [
  {
    id: 'search-to-map',
    title: 'Build your first search',
    summary: 'Build a search the quick way or by hand, fine-tune titles, keywords and personas, and put everyone you find into a project.',
    minutes: 6,
    startPath: '/search',
    needsSearch: true,
    steps: [
      {
        id: 'two-ways',
        route: '/search',
        target: ['search-describe', 'search-filters'],
        title: 'Two ways to build a search',
        placement: 'center',
        body: 'Describe who you want in a sentence and MarketMapr sets the filters for you — or set the filters yourself on the left. Either way you end up with filters you can check and change. We’ll describe it this time.',
        advance: { kind: 'next' },
      },
      {
        id: 'describe',
        route: '/search',
        target: 'search-describe',
        title: 'Describe who you’re looking for',
        body: 'Press Type it for me and we’ll describe this lesson’s search: AI engineers in Denmark. No need to be very precise — mapping the people you find is free, and once they’re in a project there are much easier ways to filter them down.',
        advance: { kind: 'event', name: 'lesson-prompt-typed' },
        action: { label: 'Type it for me', name: 'search-type-prompt' },
      },
      {
        id: 'build',
        route: '/search',
        target: 'search-describe',
        title: 'Build the search',
        body: 'Now press Build search. Your sentence becomes filters in a moment — nothing is searched yet, so you can check them first.',
        advance: { kind: 'event', name: 'search-built' },
        hint: 'Waiting for you to press Build search…',
      },
      {
        id: 'filters',
        route: '/search',
        target: 'search-filters',
        title: 'Your filters',
        body: 'Your sentence became these filters. Click any heading — Job titles, Skills, Location, tenure and more — to open it and check what’s set, add terms or remove them. The number on a heading shows how many filters it has applied.',
        advance: { kind: 'next' },
      },
      {
        id: 'remove-title',
        route: '/search',
        target: 'search-filter-titles',
        title: 'Remove a title that’s too broad',
        body: '“Data scientist” would pull in lots of people who don’t build AI. Click the × on its tag — or press Remove it for me.',
        advance: { kind: 'event', name: 'lesson-title-removed' },
        action: { label: 'Remove it for me', name: 'search-remove-title' },
      },
      {
        id: 'title-syntax',
        route: '/search',
        target: 'search-title-tip',
        title: 'Match titles your way',
        body: 'The ⓘ next to Job titles shows the three ways a title can match. Press Show me, or click the ⓘ yourself.',
        advance: { kind: 'event', name: 'lesson-title-tip-opened' },
        action: { label: 'Show me', name: 'search-open-title-tip' },
      },
      {
        id: 'title-syntax-read',
        route: '/search',
        target: 'search-title-tip-panel',
        title: 'Three ways to match a title',
        body: 'Plain words match in any order — sales director finds “Director of Sales”. Quotes keep the words together, in that order. An = sign means the whole title is exactly that, nothing more. Add several titles and a person matches if any of them do.',
        advance: { kind: 'next' },
      },
      {
        id: 'add-title',
        route: '/search',
        target: 'search-filter-titles',
        title: 'Add a title',
        body: 'LLM engineers are AI engineers too. Type “llm engineer” in the box and press Enter — or press Add it for me. Someone matching any title in the list counts, so each title you add widens the search. Not sure what else people are called? ✨ Suggest related titles under the list offers the industry’s other names for the same person.',
        advance: { kind: 'event', name: 'lesson-title-added' },
        action: { label: 'Add it for me', name: 'search-add-title' },
      },
      {
        id: 'keywords',
        route: '/search',
        placement: 'side',
        target: 'search-filter-keywords',
        title: 'Keywords: words that must appear',
        body: 'Titles say what someone is called; keywords say what they actually do. A keyword group is a list of words — any one of them counts — and you tick where to look: the headline, the summary, the skills list, the current role description or past ones. Add a second group and someone must match both, so “C#” in one group and “SQL, JavaScript” in another means C# AND (SQL or JavaScript). Add “generative ai, llm” looked for in the headline and summary — or press Add them for me.',
        advance: { kind: 'event', name: 'lesson-keyword-added' },
        action: { label: 'Add them for me', name: 'search-add-keyword-group' },
      },
      {
        id: 'suggest',
        route: '/search',
        placement: 'side',
        target: 'search-keyword-suggest',
        title: 'Let it suggest the related words',
        body: 'People describe the same thing in different words — large language models, GenAI, foundation models. Press ✨ Suggest related under the group and MarketMapr lists the industry’s other names for what you typed. Click a word to add it, or Add all. Nothing changes until you click.',
        advance: { kind: 'event', name: 'lesson-suggest-shown' },
        action: { label: 'Suggest for me', name: 'search-suggest-keywords' },
        hint: 'Waiting for the suggestions…',
      },
      {
        id: 'personas',
        route: '/search',
        target: 'search-personas',
        title: 'Several searches in one: personas',
        body: 'Personas run different searches side by side — say AI engineers, plus ML researchers with GenAI in their headline — and combine everyone into one result. Each persona has its own filters, and when you name a persona, that name becomes a tag on the people it found when you add them to a project. Press + Personas, or Turn them on for me.',
        advance: { kind: 'event', name: 'lesson-personas-on' },
        action: { label: 'Turn them on for me', name: 'search-enable-personas' },
      },
      {
        id: 'duplicate',
        route: '/search',
        placement: 'side',
        target: 'search-persona-duplicate',
        title: 'Copy a persona to try a variation',
        body: 'The quickest second persona is a copy of the first. Press ⧉ duplicate and you get the same titles, keywords and location to tweak — for example the same keywords looked for in role descriptions instead of the headline, or a wider location. Rename the copy by typing over its name; remove it with the ×. Press ⧉ duplicate, or Copy it for me.',
        advance: { kind: 'event', name: 'lesson-persona-duplicated' },
        action: { label: 'Copy it for me', name: 'search-duplicate-persona' },
      },
      {
        id: 'companies',
        route: '/search',
        target: 'search-mode-toggle',
        title: 'Filter by the companies too',
        body: 'Switch to Companies to filter by employer — size, industry, description, specialties and more. Company filters carry over: come back to People and you’ll only see people at companies that match. Your personas come with you — each persona keeps its own company filters. To target specific companies, list them under Company in the People filters — names, domains or LinkedIn URLs — or use Add in bulk to paste or upload a CSV of up to 25,000.',
        advance: { kind: 'next' },
      },
      {
        id: 'run',
        route: '/search',
        target: 'search-run',
        title: 'Run the search',
        body: 'Press Search to see who matches.',
        advance: { kind: 'event', name: 'search-results' },
        hint: 'Waiting for you to press Search…',
      },
      {
        id: 'results',
        route: '/search',
        target: 'search-results',
        title: 'Your matches',
        body: 'In this lesson these are saved results, so they appear straight away — your own searches run live across the full database. The number at the top is everyone who matches; click any row to open the full profile, including their career history.',
        advance: { kind: 'next' },
      },
      {
        id: 'add',
        route: '/search',
        target: 'search-add-project',
        title: 'Put them in a project',
        body: 'Press Add to project. Everyone taking this lesson gets the same sample project, “AI Engineers in Denmark (sample)” — one per account, so if you or a teammate already has it, we’ll open it. On your own searches you name the project yourself, and your plan sets how many people one project can take.',
        advance: { kind: 'event', name: 'search-import-started' },
        hint: 'Waiting for you to press Add to project…',
      },
      {
        id: 'importing',
        route: '/search',
        title: 'Building your project',
        body: 'People are being added now. Your project opens by itself when it’s ready — this lesson carries on there.',
        advance: { kind: 'event', name: 'search-import-done' },
        hint: 'Adding people…',
      },
      {
        id: 'done',
        route: '/project/',
        placement: 'center',
        title: 'You’ve built your first search 🎉',
        body: 'You described a market, fine-tuned the filters and put everyone into a project — this one is yours to keep. The Projects lesson (coming soon) shows how to map, filter and work with the people in it.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'find-hiring-managers',
    title: 'Find hiring managers',
    summary: 'Let Auto-discover find the people who hire at every company in a project, or search for them yourself — by persona, by location — add them as hiring managers, and fill any gaps one company at a time.',
    minutes: 5,
    startPath: '/projects',
    startOnSampleProject: true,
    needsSearch: true,
    steps: [
      {
        id: 'hm-lists',
        route: '/project/',
        target: 'project-data-tabs',
        title: 'Three lists in every project',
        body: 'Candidates are the people you map, Companies are where they work, and Hiring Managers are the people who hire at those companies. This lesson fills the Hiring Managers list, using your sample project.',
        advance: { kind: 'next' },
      },
      {
        id: 'hm-companies-tab',
        route: '/project/',
        target: 'project-tab-companies',
        title: 'Open Companies',
        body: 'Click Companies to see every company your people work for.',
        advance: { kind: 'click' },
      },
      {
        id: 'hm-select-companies',
        route: '/project/',
        target: 'project-select-all',
        title: 'Select the companies',
        body: 'Tick the box at the top to select every company on the page — or press Select them for me. If the project has more companies than fit on one page, the next step still searches all of them.',
        advance: { kind: 'event', name: 'lesson-companies-selected' },
        action: { label: 'Select them for me', name: 'project-select-all-companies' },
      },
      {
        id: 'hm-find-contacts',
        route: '/project/',
        target: 'project-find-contacts',
        title: 'Find contacts',
        body: 'Press Find Contacts to search the people who currently work at the companies you selected.',
        advance: { kind: 'click' },
      },
      {
        id: 'hm-auto',
        route: '/project/',
        target: 'contacts-intent-discover',
        title: 'The quick way: Auto-discover',
        body: 'Auto-discover reads the candidates already in your project to learn what they do, then finds the people at each selected company who hire them: hiring managers by level, talent acquisition, and the owners at smaller firms. You see what it learned first and can correct it in a sentence (“I place MEP project managers”) — do that if one title covers several trades in your project. Each company is judged by the candidates mapped there. Every plan includes free discoveries each month (Starter 1,000 · Growth 3,000 · Scale 10,000 companies); after that it’s 1 credit per company where it finds someone. On a trial, the first run of up to 25 companies is free. The people it adds are grouped at the top of the Hiring Managers list, each with a kind, a confidence and a line saying why. Team members and viewers press the same button and are asked to request approval from a team admin, who approves from an email. This lesson takes the hands-on route instead.',
        advance: { kind: 'next' },
      },
      {
        id: 'hm-intent',
        route: '/project/',
        target: 'contacts-intent-hm',
        title: 'Hiring managers, not candidates',
        body: 'You can look for more candidates or for hiring managers at these companies. Choose Hiring managers to build the search yourself.',
        advance: { kind: 'click' },
      },
      {
        id: 'hm-cs-intro',
        route: '/contact-search',
        target: 'cs-seed',
        title: 'Your companies are loaded',
        body: 'Every company you selected is now the frame of this search — the results are people who currently work there, starting with everyone. From here it works just like a candidate search.',
        advance: { kind: 'next' },
      },
      {
        id: 'hm-two-ways',
        route: '/contact-search',
        target: ['cs-describe', 'cs-filters'],
        title: 'Describe it, or set filters',
        body: 'Describe who you’re after — say “engineering leaders and talent acquisition” — and press Build search, or set the filters on the left yourself. Both work exactly as in Search.',
        advance: { kind: 'next' },
      },
      {
        id: 'hm-personas',
        route: '/contact-search',
        target: 'cs-personas-btn',
        title: 'One search for each kind of hiring manager',
        body: 'Personas run several searches at once and combine the results. We’ll set up three, each with its own job titles: C-Level, Talent Acquisition and AI Engineer Direct Hiring Managers. Or press + Personas and build your own.',
        advance: { kind: 'event', name: 'cs-personas-ready' },
        action: { label: 'Set them up for me', name: 'cs-setup-personas' },
      },
      {
        id: 'hm-persona-tabs',
        route: '/contact-search',
        target: 'cs-persona-tabs',
        title: 'Check each persona',
        body: 'Click a persona to see its filters and change them — add or remove titles, or rename it. Everyone who matches any persona appears in the results, marked with that persona’s colour.',
        advance: { kind: 'next' },
      },
      {
        id: 'hm-review-titles',
        route: '/contact-search',
        target: 'cs-filter-titles',
        title: 'Remove titles you don’t want',
        body: 'Look over each persona’s job titles before searching. “Engineering manager” in AI Engineer Direct Hiring Managers is too broad — it would bring in managers of teams that aren’t AI. Open that persona and click the × on its tag — or press Remove it for me.',
        advance: { kind: 'event', name: 'cs-title-removed' },
        action: { label: 'Remove it for me', name: 'cs-remove-title' },
      },
      {
        id: 'hm-location',
        route: '/contact-search',
        target: 'cs-filter-location',
        title: 'Choose a location',
        body: 'Narrow each persona to where you want hiring managers. Add Denmark to every persona — or type a location into each persona’s Location filter yourself.',
        advance: { kind: 'event', name: 'cs-location-set' },
        action: { label: 'Add Denmark for me', name: 'cs-add-location' },
      },
      {
        id: 'hm-run',
        route: '/contact-search',
        target: 'cs-run',
        title: 'Search all personas',
        body: 'Press Search to run all three at once.',
        advance: { kind: 'event', name: 'cs-results' },
        hint: 'Waiting for you to press Search…',
      },
      {
        id: 'hm-select',
        route: '/contact-search',
        target: 'cs-select',
        title: 'Select the results',
        body: 'Tick the box at the top of the list to select everyone on this page; when there are more, a bar offers Select all matches. Untick anyone who isn’t right — or press Select them all for me.',
        advance: { kind: 'event', name: 'cs-selected' },
        action: { label: 'Select them all for me', name: 'cs-select-all' },
      },
      {
        id: 'hm-add',
        route: '/contact-search',
        target: 'cs-add-hm',
        title: 'Add them as hiring managers',
        body: 'Press Add as hiring managers. Each one is linked to the company they work for in your project.',
        advance: { kind: 'event', name: 'cs-hm-started' },
        hint: 'Waiting for you to add them…',
      },
      {
        id: 'hm-adding',
        route: '/contact-search',
        title: 'Adding your hiring managers',
        body: 'The progress bar shows them going in. Your project opens by itself when it’s done — the lesson carries on there.',
        advance: { kind: 'event', name: 'cs-hm-added' },
        hint: 'Adding…',
      },
      {
        id: 'hm-added',
        route: '/project/',
        target: 'project-data-tabs',
        title: 'Your hiring managers',
        body: 'You’re back in the project, on the Hiring Managers list — everyone you just added, ready to filter, enrich with contact details and work with. Anyone Auto-discover adds later appears here too, grouped at the top with a coloured kind chip (hiring manager, talent acquisition or owner), a confidence, and a Why column you can show.',
        advance: { kind: 'next' },
      },
      {
        id: 'hm-zero-tab',
        route: '/project/',
        target: 'project-tab-companies',
        title: 'Any gaps?',
        body: 'Open Companies to check every company has hiring managers.',
        advance: { kind: 'click' },
      },
      {
        id: 'hm-zero',
        route: '/project/',
        target: 'company-zero-hm',
        optional: true,
        title: 'Fill gaps one company at a time',
        body: 'A company where the search found nobody shows 0 under Hiring Managers. Click it to open that one company with all its employees, and pick the right people by hand. If every company already has hiring managers, there’s nothing to do here.',
        advance: { kind: 'next' },
      },
      {
        id: 'hm-done',
        route: '/project/',
        placement: 'center',
        title: 'You’ve found your hiring managers 🎉',
        body: 'You searched every company in your project for the people who hire, set up personas, added the matches as hiring managers, and saw how to fill any gaps one company at a time.',
        advance: { kind: 'next' },
      },
    ],
  },

  // ─── Your project (folder) ──────────────────────────────────────────
  {
    id: 'connect-claude',
    title: 'Connect Claude',
    summary: 'Add MarketMapr to Claude as a connector, then ask for counts, market breakdowns, projects and backfills in plain English. A written guide: most of it happens in Claude.',
    minutes: 4,
    startPath: '/settings?tab=integrations',
    guideOnly: true,
    steps: [
      {
        id: 'cc-what',
        route: '/settings',
        title: 'What you get',
        body: 'Ask Claude in plain English and it works on the whole MarketMapr database for you: “how many DevOps engineers are there in the UK, and who employs most of them?”, “map the PLC engineers in the Midlands into a new project”, “who’s left the companies in my Dairy project this month, and who should I call about each backfill?”. It’s part of the Growth and Scale plans; Starter accounts can try it free for a month. You need a Claude account (claude.ai or the Claude desktop app).',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-month',
        route: '/settings',
        title: 'Starter: start your free month first',
        body: 'On Starter, the account holder opens Settings → Integrations and presses “Start my month of Claude”. The 30 days run from that moment and cover the whole team. Afterwards you stay on Starter and Claude stops, unless you choose to upgrade. Growth and Scale: skip this step.',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-create',
        route: '/settings',
        title: 'Create your key',
        body: 'In MarketMapr, open Settings → Integrations and press Create API key (“Claude” is a fine name). Everyone sets up their own: your key lets Claude act as you, so projects it creates are yours, exactly as if you’d made them in the app. Teammates each create a key on their own login.',
        image: '/guides/claude/1_integrations.png',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-copy',
        route: '/settings',
        title: 'Copy the connector link',
        body: 'Straight after creating the key, press Copy connector URL (the second green button). The link is only shown once, so keep the page open until it’s in Claude (if you lose it, revoke the key and create a new one). The link contains your key: share it with nobody.',
        image: '/guides/claude/2_newkey.png',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-add',
        route: '/settings',
        title: 'Open Claude’s connectors',
        body: 'In Claude (claude.ai or the desktop app), click your name in the bottom-left corner, then Settings. Go to Customize → Connectors, press + Add (top right) and choose Add custom connector.',
        image: '/guides/claude/4_connectors.png',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-paste',
        route: '/settings',
        title: 'Name it and paste the link',
        body: 'Name it MarketMapr, paste the link into the second box and press Continue. Under Authentication leave No sign-in selected (your key is inside the link) and press Add. You do this once; it then works in every chat. Using Claude Code instead? Run the command shown under the link in MarketMapr.',
        image: '/guides/claude/5_addconnector.png',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-switch-on',
        route: '/settings',
        title: 'Set permissions to Always allow',
        body: 'Open MarketMapr in your connector list. Under Tool permissions, open the Needs approval menu next to Other tools and choose Always allow, so Claude doesn’t stop to ask before every search. Then, in a new chat, check MarketMapr is switched on in the tools menu under the message box.',
        image: '/guides/claude/7_permissions.png',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-ask',
        route: '/settings',
        title: 'Things to ask',
        body: '“How many senior planners are there in Nottingham?” · “Which companies employ the most C# developers in Leeds, and what share of the market is that?” · “Map the PLC engineers in the Midlands into a new project.” · “Who’s left the companies in my Dairy project this month, and who should I call about each backfill?” Counts, searches and market breakdowns are free and unlimited.',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-imports',
        route: '/settings',
        title: 'Imports: Claude checks the number with you',
        body: 'Before adding people to a project, Claude counts them and tells you the number; anything over 2,000 needs your yes. Imports count towards your plan’s import limits, the same as in the app. If one starts that you didn’t want, tell Claude to cancel it.',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-refresh',
        route: '/settings',
        title: 'When we add new tools: refresh',
        body: 'Claude keeps the list of MarketMapr tools it loaded until you refresh it. When we add something new, a blue “i” appears on Settings in MarketMapr and Claude mentions it too. In Claude, open Settings → Connectors → MarketMapr, click ⋯ (top right) → Refresh tools list, then set the permissions to Always allow again. If you also use MarketMapr in ChatGPT, refresh it there as well.',
        image: '/guides/claude/8_refresh.png',
        advance: { kind: 'next' },
      },
      {
        id: 'cc-keys',
        route: '/settings',
        title: 'Keep your key safe',
        body: 'Settings → Integrations lists your keys and when each was last used. Revoke one the moment you think it’s leaked and Claude stops working with it straight away; create a new one to reconnect.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'use-claude',
    title: 'Use Claude: examples and prompts',
    summary: 'See Claude at work on five real tasks (job spec to shortlist, winning a client, a daily call sheet, a market report, movers and backfills), then copy prompts for your own: find candidates, size a market, win clients, tag a project.',
    minutes: 5,
    startPath: '/training?tab=claude',
    guideOnly: true,
    opensTab: 'claude',
    steps: [
      {
        id: 'uc-watch',
        route: '/training',
        title: 'Watch the examples',
        body: 'Five worked examples play in a Claude window: the question, the MarketMapr steps Claude takes, and the answer it gives.',
        advance: { kind: 'next' },
      },
      {
        id: 'uc-copy',
        route: '/training',
        title: 'Copy a prompt',
        body: 'Pick a prompt for the job in hand, copy it into a new Claude chat and fill in the [square brackets].',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-connected-lists',
    folder: 'project',
    title: 'Three connected lists',
    summary: 'Candidates, companies and hiring managers — and why filtering one filters the others.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pcl-intro',
        route: '/project/',
        placement: 'center',
        title: 'One project, three lists',
        body: 'Every project holds three lists that are joined together: the candidates, the companies they work for, and the hiring managers (decision makers) at those companies. Filter one and everything connected to it filters too.',
        advance: { kind: 'next' },
      },
      {
        id: 'pcl-tabs',
        route: '/project/',
        target: 'project-data-tabs',
        title: 'Switch between the lists',
        body: 'Candidates, Companies and Hiring Managers — each tab shows its count. When a search or filter is on, you’ll see “N from M”: how many match out of everyone in the project.',
        advance: { kind: 'next' },
      },
      {
        id: 'pcl-open-companies',
        route: '/project/',
        target: 'project-tab-companies',
        title: 'Open Companies',
        body: 'Click Companies to see who employs your candidates.',
        advance: { kind: 'click' },
      },
      {
        id: 'pcl-candidate-count',
        route: '/project/',
        target: 'company-candidate-count',
        title: 'Who hires the most',
        body: 'This column is how many of your candidates work at each company — the list is ranked by it, so the biggest employers come first. Click a number to see exactly who works there.',
        advance: { kind: 'event', name: 'company-candidates-opened' },
        hint: 'Click a number in the Candidates column.',
      },
      {
        id: 'pcl-company-people',
        route: '/project/',
        target: 'association-banner',
        optional: true,
        title: 'Just that company’s people',
        body: 'You’re back on Candidates, showing only the people at that company. “Clear filter” takes you back to everyone.',
        advance: { kind: 'next' },
      },
      {
        id: 'pcl-managers',
        route: '/project/',
        target: 'project-tab-managers',
        title: 'Hiring managers are connected too',
        body: 'Hiring managers you add are linked to their companies the same way — the Companies tab counts them, and clicking a count lists them. Filter candidates (say, by a skill) and the Companies and Hiring Managers tabs narrow to just the companies those people work at.',
        advance: { kind: 'next' },
      },
      {
        id: 'pcl-done',
        route: '/project/',
        placement: 'center',
        title: 'That’s the model 🎉',
        body: 'Three lists, joined: whatever you filter on — candidates or companies — the other lists follow. The next lessons show the ways to view, search and filter them.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-ways-to-view',
    folder: 'project',
    title: 'Ways to view your data',
    summary: 'Table, cards, and the profile panel — with arrow keys to move between records.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pwv-toggle',
        route: '/project/',
        target: ['view-table', 'view-card'],
        title: 'Table or cards',
        body: 'The table breaks every detail into its own column — scan along with your mouse or the scrollbar. Cards show each record like a LinkedIn search result.',
        advance: { kind: 'next' },
      },
      {
        id: 'pwv-cards',
        route: '/project/',
        target: 'view-card',
        title: 'Switch to cards',
        body: 'Click the card icon.',
        advance: { kind: 'event', name: 'view-mode-card' },
      },
      {
        id: 'pwv-open',
        route: '/project/',
        target: 'record-card',
        title: 'Open a profile',
        body: 'Click any card to open the full profile — everything about that candidate, company or hiring manager in one place. (In the table, clicking a name does the same.)',
        advance: { kind: 'event', name: 'profile-opened' },
        hint: 'Click a card.',
      },
      {
        id: 'pwv-move',
        route: '/project/',
        target: 'profile-next',
        title: 'Move between records',
        body: 'Use the → and ← arrow keys (or the arrow buttons up here) to go to the next or previous record without closing the profile. ↑ and ↓ scroll the profile.',
        advance: { kind: 'event', name: 'profile-moved' },
        hint: 'Press → on your keyboard.',
      },
      {
        id: 'pwv-back',
        route: '/project/',
        placement: 'center',
        title: 'Back to the table',
        body: 'Close the profile and switch back to the table whenever you like — your view is remembered next time.',
        action: { label: 'Take me back to the table', name: 'lesson-back-to-table' },
        advance: { kind: 'event', name: 'view-mode-table' },
        hint: 'Or click the table icon yourself.',
      },
      {
        id: 'pwv-done',
        route: '/project/',
        placement: 'center',
        title: 'You know your way around 🎉',
        body: 'Table for scanning and comparing, cards for browsing, and the profile for the full picture — with arrow keys to work through a list quickly.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-quick-search',
    folder: 'project',
    title: 'Quick search',
    summary: 'The fastest way to find someone by name — it looks in every column at once.',
    minutes: 1,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pqs-type',
        route: '/project/',
        target: 'project-quick-search',
        title: 'Find someone fast',
        body: 'Type a name here. Quick search looks through every column — names, emails, titles, companies, skills, career history — and keeps only the records with a match.',
        action: { label: 'Type one for me', name: 'lesson-quick-search-example' },
        advance: { kind: 'event', name: 'quick-search-done' },
        hint: 'Type a name, or press “Type one for me”.',
      },
      {
        id: 'pqs-results',
        route: '/project/',
        target: 'project-data-tabs',
        title: 'Everything follows the search',
        body: 'All three tabs now count only the matches. Look for more than one thing at once by separating words with commas or OR — e.g. “Anna, Lars”.',
        advance: { kind: 'next' },
      },
      {
        id: 'pqs-clear',
        route: '/project/',
        target: 'quick-search-clear',
        title: 'Clear it',
        body: 'Click ✕ to see everyone again.',
        advance: { kind: 'event', name: 'quick-search-cleared' },
      },
      {
        id: 'pqs-done',
        route: '/project/',
        placement: 'center',
        title: 'Done 🎉',
        body: 'Quick search is for finding a person or a word. To describe what you’re looking for — “people with GenAI experience” — use AI Smart Search, in the next lesson.',
        advance: { kind: 'next' },
      },
    ],
  },

  {
    id: 'project-ai-search',
    folder: 'project',
    title: 'AI Smart Search',
    summary: 'Describe what you want in plain English — MarketMapr turns it into keyword filters you can review, then highlights every match.',
    minutes: 3,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pai-describe',
        route: '/project/',
        target: ['ai-search-input', 'ai-search-run'],
        title: 'Describe what you’re looking for',
        body: 'Write it the way you’d say it — e.g. “Find me people with experience in GenAI” — then click Search. You can paste a whole Boolean string here too. Nothing runs yet: you’ll check the filters first.',
        action: { label: 'Type the example for me', name: 'lesson-ai-search-example' },
        advance: { kind: 'event', name: 'ai-preview-opened' },
        hint: 'Type it (or press “Type the example for me”), then click Search.',
      },
      {
        id: 'pai-keywords',
        route: '/project/',
        target: 'ai-preview-keywords',
        title: 'Your keywords',
        body: 'These are the keywords it will look for — including related terms worth searching (for GenAI: LLM, generative AI…). Remove any with ✕ or add your own.',
        advance: { kind: 'next' },
      },
      {
        id: 'pai-columns',
        route: '/project/',
        target: 'ai-preview-columns',
        title: 'Where to look',
        body: 'These are the columns it will search. Remove any you don’t want — or search every column.',
        advance: { kind: 'next' },
      },
      {
        id: 'pai-sample',
        route: '/project/',
        target: 'ai-preview-sample',
        title: 'Pick sections on a sample profile',
        body: 'Prefer to point? Click parts of this sample profile to switch them on or off. Tip for business development: search only recent sections — headline, current job title, current role description — and the Companies tab shows every company employing people with that experience right now.',
        advance: { kind: 'next' },
      },
      {
        id: 'pai-apply',
        route: '/project/',
        target: 'ai-preview-run',
        title: 'Run it',
        body: 'Click Run Search → to apply the filters.',
        advance: { kind: 'event', name: 'ai-search-applied' },
      },
      {
        id: 'pai-results',
        route: '/project/',
        target: 'ai-search-summary',
        optional: true,
        title: 'Filters added — matches highlighted',
        body: 'The search is now ordinary filters you can see and edit. In the table (and in each profile) the matching words are highlighted, so you can see at a glance where each match was found.',
        advance: { kind: 'next' },
      },
      {
        id: 'pai-done',
        route: '/project/',
        placement: 'center',
        title: 'Search by meaning, not just names 🎉',
        body: 'Use AI Smart Search whenever you’d otherwise build filters by hand. Save the search so you can reapply it in one click next time — and “Clear AI Search” takes you back to everyone.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-filters',
    folder: 'project',
    title: 'Filters and filter groups',
    summary: 'Build exact filters on candidate or company data — OR inside a group, AND between groups — or filter straight from a column.',
    minutes: 3,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pf-open',
        route: '/project/',
        target: 'filters-button',
        title: 'Open Filters',
        body: 'When you know exactly what you want, build the filters yourself. Click Filters.',
        advance: { kind: 'click' },
      },
      {
        id: 'pf-model',
        route: '/project/',
        target: 'filter-groups-panel',
        title: 'Groups and conditions',
        body: 'Filters are built from groups. Inside a group, conditions are joined by OR (any can match). Between groups it’s AND (every group must match). That’s Boolean logic without writing a string.',
        advance: { kind: 'next' },
      },
      {
        id: 'pf-add-group',
        route: '/project/',
        target: 'filters-add-candidate-group',
        title: 'Add a filter group',
        body: 'Choose candidate data or company data. Click “+ Add Candidate Filter Group”.',
        advance: { kind: 'click' },
      },
      {
        id: 'pf-condition',
        route: '/project/',
        target: 'filter-condition',
        title: 'Your first condition',
        body: 'Pick a column and what it should contain — e.g. Current role, contains, “Engineer”.',
        advance: { kind: 'next' },
      },
      {
        id: 'pf-add-condition',
        route: '/project/',
        target: 'filter-add-condition',
        title: 'Add a condition (OR)',
        body: 'Add a second condition to the same group — e.g. Current role contains “Developer” — to match either.',
        advance: { kind: 'click' },
      },
      {
        id: 'pf-logic',
        route: '/project/',
        target: 'filter-logic',
        title: 'Any or all',
        body: 'This switch decides how the group’s conditions combine: OR (any) or AND (all).',
        advance: { kind: 'next' },
      },
      {
        id: 'pf-second-group',
        route: '/project/',
        target: 'filters-add-candidate-group',
        title: 'A new group means AND',
        body: 'Add another group — e.g. Current role description contains “GenAI”. Together: (Engineer OR Developer) AND GenAI.',
        advance: { kind: 'next' },
      },
      {
        id: 'pf-apply',
        route: '/project/',
        target: 'filters-apply',
        title: 'Apply',
        body: 'Click Apply Filters. Short on time? Let us build that exact example for you.',
        action: { label: 'Build the example for me', name: 'lesson-filters-example' },
        advance: { kind: 'event', name: 'filters-applied' },
      },
      {
        id: 'pf-companies',
        route: '/project/',
        target: 'project-tab-companies',
        title: 'Companies follow your filters',
        body: 'Click Companies — only the companies employing matching candidates remain.',
        advance: { kind: 'click' },
      },
      {
        id: 'pf-company-counts',
        route: '/project/',
        target: 'company-candidate-count',
        title: 'How many match at each company',
        body: 'The count now reads matched/total — e.g. 4/15 means 4 of their 15 people in your project match. Companies are ranked by it, so the biggest employers of exactly these people come first.',
        advance: { kind: 'next' },
      },
      {
        id: 'pf-column',
        route: '/project/',
        placement: 'side',
        target: 'column-menu',
        title: 'Filter from any column',
        body: 'Quicker still: click ⋮ on a column, choose “Filter on this column” and type a word — e.g. Industry contains “Financial Services”. Candidates and hiring managers at those companies filter with it.',
        advance: { kind: 'next' },
      },
      {
        id: 'pf-done',
        route: '/project/',
        placement: 'center',
        title: 'Exactly the people you want 🎉',
        body: 'Groups for AND, conditions for OR, and a column’s ⋮ menu for a quick one-off. Save the search to reuse it, or turn the results into a subproject.',
        advance: { kind: 'next' },
      },
    ],
  },

  {
    id: 'project-employment-type',
    folder: 'project',
    title: 'Employment type',
    summary: 'Filter to people in contract or freelance roles — and see which companies hire them most.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pet-open',
        route: '/project/',
        target: 'employment-type-button',
        title: 'Open Employment Type',
        body: 'Click Employment Type.',
        advance: { kind: 'click' },
      },
      {
        id: 'pet-pick',
        route: '/project/',
        target: 'employment-type-panel',
        title: 'Pick the types you want',
        body: 'Every employment type in this project is listed. Tick Contract and Freelance, then click Apply.',
        action: { label: 'Tick them for me', name: 'lesson-employment-example' },
        advance: { kind: 'event', name: 'filters-applied' },
        hint: 'Tick the boxes, then Apply.',
      },
      {
        id: 'pet-candidates',
        route: '/project/',
        target: 'project-data-tabs',
        title: 'People in those roles now',
        body: 'Only candidates currently working in a contract or freelance role remain — including people whose second, concurrent role is a contract.',
        advance: { kind: 'next' },
      },
      {
        id: 'pet-companies',
        route: '/project/',
        target: 'project-tab-companies',
        title: 'Who hires them?',
        body: 'Click Companies.',
        advance: { kind: 'click' },
      },
      {
        id: 'pet-counts',
        route: '/project/',
        target: 'company-candidate-count',
        title: 'The biggest users of contractors',
        body: 'Each count reads matched/total — e.g. 4/15 means 4 of the 15 people you have there are on contract. Ranked from the most to the least, so the companies that hire contractors most come first.',
        advance: { kind: 'next' },
      },
      {
        id: 'pet-done',
        route: '/project/',
        placement: 'center',
        title: 'A ready-made BD list 🎉',
        body: 'Employment type works with every other filter — combine it with a skill or a location, then save the search.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-radius',
    folder: 'project',
    title: 'Radius search',
    summary: 'Find people who live within a distance of a place — or companies with an office there.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pr-open',
        route: '/project/',
        target: 'radius-button',
        title: 'Open Radius search',
        body: 'Every candidate’s location and every company office is placed on the map, so you can search by distance. Click Radius search.',
        advance: { kind: 'click' },
      },
      {
        id: 'pr-two',
        route: '/project/',
        target: ['radius-company', 'radius-candidate'],
        title: 'Two kinds of location',
        body: 'Company Office Location finds companies with an office near a place — and the candidates who work there. Candidate Personal Location finds people who live near it, wherever they work.',
        advance: { kind: 'next' },
      },
      {
        id: 'pr-candidate',
        route: '/project/',
        target: 'radius-candidate',
        title: 'Search around a place',
        body: 'Type a place and a distance in miles, then click Apply Candidate Filter.',
        action: { label: '15 miles of Copenhagen for me', name: 'lesson-radius-example' },
        advance: { kind: 'event', name: 'radius-applied' },
        hint: 'Fill it in (or press the button), then Apply Candidate Filter.',
      },
      {
        id: 'pr-results',
        route: '/project/',
        target: 'project-data-tabs',
        title: 'Everyone within reach',
        body: 'Only people within that distance remain — and the Companies and Hiring Managers tabs follow.',
        advance: { kind: 'next' },
      },
      {
        id: 'pr-done',
        route: '/project/',
        placement: 'center',
        title: 'Location, sorted 🎉',
        body: 'Combine a radius with any filter — “contractors within 20 miles of Aarhus” — and save it to reuse.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-save-search',
    folder: 'project',
    title: 'Save a search',
    summary: 'Keep a set of filters under a name and reapply it in one click, any day.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pss-filter',
        route: '/project/',
        placement: 'center',
        title: 'Start with some filters',
        body: 'You save whatever filters are on. Let’s add an example: (Engineer OR Developer) AND GenAI.',
        action: { label: 'Add the example filters', name: 'lesson-filters-example' },
        advance: { kind: 'event', name: 'filters-applied' },
      },
      {
        id: 'pss-save',
        route: '/project/',
        target: 'save-search-button',
        title: 'Save Search',
        body: 'Click Save Search.',
        advance: { kind: 'click' },
      },
      {
        id: 'pss-name',
        route: '/project/',
        target: ['save-search-name', 'save-search-confirm'],
        title: 'Name it',
        body: 'Give it a name you’ll recognise, then click Save. It keeps your filters, employment types, radius and sort.',
        action: { label: 'Name it for me', name: 'lesson-save-search-name' },
        advance: { kind: 'event', name: 'search-saved' },
        hint: 'Type a name, then Save.',
      },
      {
        id: 'pss-load',
        route: '/project/',
        target: 'saved-searches-button',
        title: 'Your saved searches',
        body: 'They live here. Come back any day, click one, and exactly the same filters are applied — no rebuilding.',
        advance: { kind: 'next' },
      },
      {
        id: 'pss-done',
        route: '/project/',
        placement: 'center',
        title: 'Saved 🎉',
        body: 'Save the searches you run often — a market you track, a role you hire for — and they’re one click away.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-freeze-formulas',
    folder: 'project',
    title: 'Freeze columns and formulas',
    summary: 'Keep a column in view while you scroll, and create new columns by describing what you want.',
    minutes: 3,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pff-menu',
        route: '/project/',
        placement: 'side',
        target: 'column-menu',
        title: 'Every column has a menu',
        body: 'Click ⋮ on this column.',
        advance: { kind: 'click' },
      },
      {
        id: 'pff-freeze',
        route: '/project/',
        placement: 'side',
        target: 'column-menu-freeze',
        title: 'Freeze it',
        body: 'Click “Freeze column”. It stays in view however far right you scroll — handy for keeping names on screen.',
        advance: { kind: 'event', name: 'column-frozen' },
      },
      {
        id: 'pff-menu-2',
        route: '/project/',
        placement: 'side',
        target: 'column-menu',
        title: 'Now a formula',
        body: 'Formulas build a new column from the data you already have, row by row. Open the menu again.',
        advance: { kind: 'click' },
      },
      {
        id: 'pff-insert',
        route: '/project/',
        placement: 'side',
        target: 'column-menu-formula',
        title: 'Insert formula column',
        body: 'Click “Insert formula column”.',
        advance: { kind: 'event', name: 'formula-opened' },
      },
      {
        id: 'pff-describe',
        route: '/project/',
        target: ['formula-name', 'formula-prompt'],
        title: 'Describe it',
        body: 'Name the column and say what to calculate — type / to drop in a column. E.g. “Yes if their headline mentions GenAI, otherwise No”. Then click Generate.',
        action: { label: 'Write the example for me', name: 'lesson-formula-example' },
        advance: { kind: 'event', name: 'formula-generated' },
        hint: 'Describe it, then Generate.',
      },
      {
        id: 'pff-save',
        route: '/project/',
        target: 'formula-save',
        title: 'Check and save',
        body: 'You’ll see the formula and a live preview on your first rows. Happy? Click Save column — it fills in for every row, and formulas use no credits.',
        advance: { kind: 'event', name: 'formula-saved' },
      },
      {
        id: 'pff-done',
        route: '/project/',
        placement: 'center',
        title: 'What formulas are good for 🎉',
        body: 'Pull out a piece (the domain from an email, the first word of a title), combine fields into one, clean up messy titles, flag who meets your criteria, or score candidates “Good fit” / “Not a fit”.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-ai-columns',
    folder: 'project',
    title: 'AI columns',
    summary: 'Ask a question of every row — like handing your table to Claude — and get the answer in a new column.',
    minutes: 3,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pac-why',
        route: '/project/',
        placement: 'center',
        title: 'When keywords aren’t enough',
        body: 'A keyword tells you a word appears. An AI column reads each record and answers a question — “has this person built a data centre over 50MW?” — which no keyword can. Think of it as uploading your table to Claude with a prompt.',
        advance: { kind: 'next' },
      },
      {
        id: 'pac-menu',
        route: '/project/',
        placement: 'side',
        target: 'column-menu',
        title: 'Open a column’s menu',
        body: 'Click ⋮ — the new column goes next to this one.',
        advance: { kind: 'click' },
      },
      {
        id: 'pac-insert',
        route: '/project/',
        placement: 'side',
        target: 'column-menu-ai',
        title: 'Insert AI column',
        body: 'Click “Insert AI column”.',
        advance: { kind: 'event', name: 'aicol-opened' },
      },
      {
        id: 'pac-name',
        route: '/project/',
        target: 'aicol-name',
        title: 'Name it',
        body: 'Call it what it answers, e.g. “Built a 50MW+ data centre?”.',
        action: { label: 'Fill in the example for me', name: 'lesson-aicol-example' },
        advance: { kind: 'next' },
      },
      {
        id: 'pac-output',
        route: '/project/',
        target: 'aicol-output',
        title: 'Choose the answer type',
        body: 'Category: one of a list you set (Yes / No / Unclear) — easy to filter. Text: a short written answer. Score: a number in a range you choose — easy to sort.',
        advance: { kind: 'next' },
      },
      {
        id: 'pac-instruction',
        route: '/project/',
        target: 'aicol-instruction',
        title: 'Your instruction',
        body: 'This is the prompt: ask exactly what you want to know, as you would a colleague reading the profile.',
        advance: { kind: 'next' },
      },
      {
        id: 'pac-fields',
        route: '/project/',
        target: 'aicol-fields',
        title: 'What it reads',
        body: 'Pick the fields it should read. Only what’s needed — the more it reads, the more it costs.',
        advance: { kind: 'next' },
      },
      {
        id: 'pac-model',
        route: '/project/',
        target: 'aicol-model',
        title: 'Choose a model',
        body: 'Haiku is fast and cheap — right for most questions. Sonnet is better at nuanced judgement.',
        advance: { kind: 'next' },
      },
      {
        id: 'pac-preview',
        route: '/project/',
        target: 'aicol-preview',
        title: 'Preview before you spend',
        body: 'Preview runs it on 10 rows so you can check the answers, and shows what the full run would cost in credits — nothing runs on everyone until you say so. Tip: filter first, then add the AI column, to run it on just those results.',
        advance: { kind: 'next' },
      },
      {
        id: 'pac-done',
        route: '/project/',
        placement: 'center',
        title: 'Research every row at once 🎉',
        body: 'Preview when you’re ready, or Cancel for now. Answers land in the new column, ready to filter and sort.',
        advance: { kind: 'next' },
      },
    ],
  },

  {
    id: 'project-companies-tab',
    folder: 'project',
    title: 'The Companies tab',
    summary: 'Who employs your candidates, ranked — with candidate and hiring-manager counts you can click into.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pct-open',
        route: '/project/',
        target: 'project-tab-companies',
        title: 'Open Companies',
        body: 'Click Companies.',
        advance: { kind: 'click' },
      },
      {
        id: 'pct-ranked',
        route: '/project/',
        target: 'company-candidate-count',
        title: 'Ranked by who hires the most',
        body: 'Companies are ranked by how many of your candidates work there. Click a count any time to see exactly who.',
        advance: { kind: 'next' },
      },
      {
        id: 'pct-hms',
        route: '/project/',
        target: 'company-hm-count',
        optional: true,
        title: 'Hiring managers at each company',
        body: 'How many hiring managers you’ve found there — click a count to list them. A 0 means none yet: click it to find some.',
        advance: { kind: 'next' },
      },
      {
        id: 'pct-follow',
        route: '/project/',
        placement: 'center',
        title: 'Everything follows your filters',
        body: 'Filter candidates (say, GenAI in their headline) and each count becomes matched/total — how many of that company’s people match. You can also filter on company data (industry, size, location) and add AI columns that research the companies themselves.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-competitors',
    folder: 'project',
    title: 'Find competitors',
    summary: 'One click finds companies like any company in your project — then widen or fine-tune the match.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pcm-open',
        route: '/project/',
        target: 'project-tab-companies',
        title: 'Open Companies',
        body: 'Click Companies.',
        advance: { kind: 'click' },
      },
      {
        id: 'pcm-click',
        route: '/project/',
        target: 'company-competitors',
        title: 'Competitors',
        body: 'Click 🔍 Competitors next to a company. MarketMapr reads what that company does and filters for companies that do the same.',
        advance: { kind: 'event', name: 'competitors-found' },
      },
      {
        id: 'pcm-filters',
        route: '/project/',
        target: 'ai-search-summary',
        optional: true,
        title: 'The match it built',
        body: 'These are the filters it chose from the company’s profile — what it offers, its sub-industry and specialties.',
        advance: { kind: 'next' },
      },
      {
        id: 'pcm-widen',
        route: '/project/',
        target: 'widen-search',
        optional: true,
        title: 'Too tight? Widen it',
        body: 'If it finds too few, Widen Search loosens the match to companies that share any of those traits.',
        advance: { kind: 'event', name: 'search-widened' },
      },
      {
        id: 'pcm-check',
        route: '/project/',
        placement: 'center',
        title: 'Check the filters ✔',
        body: 'Tip: after widening, open Filters and remove anything too generic (like “software”) that brings in companies that aren’t really competitors.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-tags',
    folder: 'project',
    title: 'Tags',
    summary: 'Label any set of candidates or companies, and bring them back later with one click.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'ptg-select',
        route: '/project/',
        target: 'row-checkbox',
        title: 'Select some rows',
        body: 'Tick the rows you want to tag — filter first to tag a whole set at once.',
        action: { label: 'Tick three for me', name: 'lesson-select-rows' },
        advance: { kind: 'event', name: 'rows-selected' },
      },
      {
        id: 'ptg-tag',
        route: '/project/',
        target: 'tag-button',
        title: 'Tag them',
        body: 'Click Tag.',
        advance: { kind: 'click' },
      },
      {
        id: 'ptg-name',
        route: '/project/',
        target: ['tag-name', 'tag-add'],
        title: 'Name the tag',
        body: 'Type a new tag or pick an existing one, then Add Tag.',
        action: { label: 'Call it “Shortlist” for me', name: 'lesson-tag-name' },
        advance: { kind: 'event', name: 'tag-added' },
      },
      {
        id: 'ptg-click',
        route: '/project/',
        target: 'row-tag',
        title: 'Click a tag to filter',
        body: 'Any day, click a tag in the table to show everyone with it.',
        advance: { kind: 'event', name: 'tag-filter-applied' },
      },
      {
        id: 'ptg-done',
        route: '/project/',
        placement: 'center',
        title: 'Tagged 🎉',
        body: 'Tags work on candidates, companies and hiring managers — use them for shortlists, target accounts, or anything you want to come back to.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-subprojects',
    folder: 'project',
    title: 'Subprojects',
    summary: 'Turn filtered results into their own project — copied or moved — nested under the original.',
    minutes: 2,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'psp-filter',
        route: '/project/',
        placement: 'center',
        title: 'Start from filtered results',
        body: 'A subproject is made from whatever is filtered. Let’s filter to an example first.',
        action: { label: 'Add the example filters', name: 'lesson-filters-example' },
        advance: { kind: 'event', name: 'filters-applied' },
      },
      {
        id: 'psp-open',
        route: '/project/',
        target: 'create-subproject-button',
        title: 'Create a subproject',
        body: 'Click “Create New Project with Filtered Data”.',
        advance: { kind: 'click' },
      },
      {
        id: 'psp-name',
        route: '/project/',
        target: 'subproject-name',
        title: 'Name it',
        body: 'It’s nested under this project on your Projects page.',
        advance: { kind: 'next' },
      },
      {
        id: 'psp-modes',
        route: '/project/',
        target: 'subproject-modes',
        title: 'Copy or move',
        body: 'Copy keeps the records here too. Move takes them out of this project and into the new one. Or copy them into a project you already have. A smaller subproject is ideal for monitoring just the people who matter.',
        advance: { kind: 'next' },
      },
      {
        id: 'psp-done',
        route: '/project/',
        placement: 'center',
        title: 'Your call 🎉',
        body: 'Create it now, or Cancel — you can make a subproject from any filtered view whenever you need one.',
        advance: { kind: 'next' },
      },
    ],
  },
  {
    id: 'project-export',
    folder: 'project',
    title: 'Export to CSV',
    summary: 'Download what you see — and how to export a hand-picked set.',
    minutes: 1,
    startPath: '/projects',
    anyProject: true,
    steps: [
      {
        id: 'pex-button',
        route: '/project/',
        target: 'export-csv',
        title: 'Export what you see',
        body: 'Export CSV downloads the rows in your current view — so if you’ve filtered, only the filtered results — with your visible columns, in their order.',
        advance: { kind: 'next' },
      },
      {
        id: 'pex-tip',
        route: '/project/',
        placement: 'center',
        title: 'Exporting hand-picked rows',
        body: 'Tick the rows you want, tag them (see the Tags lesson), click that tag to filter to them, then Export CSV.',
        advance: { kind: 'next' },
      },
    ],
  },
];

export const lessonById = (id: string) => LESSONS.find((l) => l.id === id) || null;
